/**
 * API client do módulo Produção v2 (BRIEF DEFINITIVO Fase 4 — Sprint P1).
 *
 * Integração real com o backend P0.3 (`producao/views_v2.py`). Todas as
 * funções recebem o `request` autenticado do AuthContext (padrão de
 * services/producao.js).
 *
 * Tratamento de erro:
 *   - GETs usam `silentError: true` — as telas mostram banner inline com
 *     "Tentar de novo" (sem toast duplicado).
 *   - Mutações NÃO usam silent: o auto-toast global do `request` exibe o
 *     `payload.detalhe` do backend (deduped). Os <AsyncButton> que disparam
 *     essas mutações passam `silentError` no BOTÃO para não duplicar o toast
 *     nem rethrow (que viraria unhandled rejection/overlay do CRA).
 *
 * Actions de transição retornam SEMPRE o Detalhe atualizado do ambiente
 * (o caller substitui o estado da tela com a resposta). Erro de domínio =
 * 400 {detalhe}. `cancelar` exige MFA recente — o 403 é interceptado
 * globalmente pelo `request` (modal de step-up + retry transparente).
 */

const PREFIXO_AMBIENTES = "/api/ambientes-producao";
const PREFIXO_PAINEL = "/api/producao";

// ============================================================
// Constantes de domínio (espelham producao/models.py do backend)
// ============================================================

/**
 * Fases internas -> rótulos da gestora (brief 0.6/F.1 — NÃO renomear;
 * F7/F8 não existem no vocabulário visível). PEDIDO_CRIADO e CARREGADO
 * são marcos de entrada/saída, não tabs.
 */
export const FASE_INFO = {
  PEDIDO_CRIADO: { curto: "Criado", rotulo: "Pedido criado", marco: true },
  DEFINICOES_INICIAIS: { curto: "F1", rotulo: "F1 Definições", marco: false },
  PROJETO_EXECUTIVO: { curto: "F2", rotulo: "F2 Projeto Exec.", marco: false },
  OTIMIZACAO: { curto: "F3", rotulo: "F3 Otimização", marco: false },
  COMPRAS: { curto: "F4", rotulo: "F4 Compras", marco: false },
  INDUSTRIALIZACAO: { curto: "F5", rotulo: "F5 Industrialização", marco: false },
  MARCENARIA: { curto: "F6", rotulo: "F6 Marcenaria", marco: false },
  CARREGADO: { curto: "Carregado", rotulo: "Carregado", marco: true },
};

/** Paradas do SeletorFases do Painel (só as 6 fases operacionais, na ordem). */
export const FASES_PAINEL = [
  { codigo: "DEFINICOES_INICIAIS", label: "F1 Definições" },
  { codigo: "PROJETO_EXECUTIVO", label: "F2 Projeto Exec." },
  { codigo: "OTIMIZACAO", label: "F3 Otimização" },
  { codigo: "COMPRAS", label: "F4 Compras" },
  { codigo: "INDUSTRIALIZACAO", label: "F5 Industrialização" },
  { codigo: "MARCENARIA", label: "F6 Marcenaria" },
];

/** Motivos tipados de congelamento + TTL default (dias) aplicado pelo backend. */
export const MOTIVOS_CONGELAMENTO = [
  { codigo: "CLIENTE_INDEFINICAO", label: "Indefinição do cliente", ttlDias: 15 },
  { codigo: "CLIENTE_MUDANCA_ESCOPO", label: "Mudança de escopo do cliente", ttlDias: 10 },
  { codigo: "FORNECEDOR_ATRASO", label: "Atraso de fornecedor", ttlDias: 7 },
  { codigo: "AGUARDA_TERCEIRO_OSE", label: "Aguardando terceiro (OSE)", ttlDias: null },
  { codigo: "DECISAO_INTERNA", label: "Decisão interna", ttlDias: 5 },
];

/** Categorias 6M de causa de atraso (alimenta Ishikawa/Pareto — D12). */
export const CATEGORIAS_6M = [
  { codigo: "MAQUINA", label: "Máquina" },
  { codigo: "MAO_DE_OBRA", label: "Mão de obra" },
  { codigo: "MATERIAL", label: "Material" },
  { codigo: "METODO", label: "Método" },
  { codigo: "MEDICAO", label: "Medição" },
  { codigo: "MEIO_EXTERNO", label: "Meio externo" },
];

/**
 * Espelho fiel de CATALOGO_ETAPAS (producao/models.py): código, fase,
 * label, ordem e bipável. Alimenta o filtro encadeado Fase→Etapa da
 * Análise de Atrasos e qualquer tela que precise da ordem do roteiro.
 */
export const CATALOGO_ETAPAS_INFO = [
  { codigo: "DEFINICAO_ACABAMENTO", fase: "DEFINICOES_INICIAIS", label: "Definição de acabamento", ordem: 1, bipavel: false },
  { codigo: "MEDICAO_TECNICA", fase: "DEFINICOES_INICIAIS", label: "Medição técnica", ordem: 2, bipavel: false },
  { codigo: "ELABORACAO", fase: "PROJETO_EXECUTIVO", label: "Elaboração", ordem: 3, bipavel: false },
  { codigo: "REVISAO", fase: "PROJETO_EXECUTIVO", label: "Revisão", ordem: 4, bipavel: false },
  { codigo: "APROVACAO", fase: "PROJETO_EXECUTIVO", label: "Aprovação", ordem: 5, bipavel: false },
  { codigo: "AGRUPAR_LOTE", fase: "OTIMIZACAO", label: "Agrupar lote", ordem: 6, bipavel: false },
  { codigo: "PLANO_CORTE", fase: "OTIMIZACAO", label: "Plano de corte", ordem: 7, bipavel: false },
  { codigo: "GERAR_BOM", fase: "OTIMIZACAO", label: "Gerar BOM", ordem: 8, bipavel: false },
  { codigo: "REQUISICAO", fase: "COMPRAS", label: "Requisição", ordem: 9, bipavel: false },
  { codigo: "RECEBIMENTO", fase: "COMPRAS", label: "Recebimento", ordem: 10, bipavel: false },
  { codigo: "CORTE", fase: "INDUSTRIALIZACAO", label: "Corte", ordem: 11, bipavel: true },
  { codigo: "BORDO", fase: "INDUSTRIALIZACAO", label: "Bordo", ordem: 12, bipavel: true },
  { codigo: "FURACAO", fase: "INDUSTRIALIZACAO", label: "Furação", ordem: 13, bipavel: true },
  { codigo: "USINAGEM", fase: "INDUSTRIALIZACAO", label: "Usinagem", ordem: 14, bipavel: true },
  { codigo: "CAIXARIA", fase: "MARCENARIA", label: "Caixaria", ordem: 15, bipavel: true },
  { codigo: "DUPLAGEM", fase: "MARCENARIA", label: "Duplagem", ordem: 16, bipavel: true },
  { codigo: "PINTURA", fase: "MARCENARIA", label: "Pintura", ordem: 17, bipavel: true },
  { codigo: "ELETRICA", fase: "MARCENARIA", label: "Elétrica", ordem: 18, bipavel: true },
  { codigo: "PRE_MONTAGEM", fase: "MARCENARIA", label: "Pré-montagem", ordem: 19, bipavel: true },
  { codigo: "CONFERENCIA", fase: "MARCENARIA", label: "Conferência", ordem: 20, bipavel: true },
  { codigo: "LIMPEZA", fase: "MARCENARIA", label: "Limpeza", ordem: 21, bipavel: true },
  { codigo: "SEPARACAO", fase: "MARCENARIA", label: "Separação", ordem: 22, bipavel: true },
  { codigo: "CARREGAMENTO", fase: "MARCENARIA", label: "Carregamento", ordem: 23, bipavel: true },
];

/** {fase: [etapas do catálogo]} — filtro Etapa encadeado à Fase. */
export const ETAPAS_POR_FASE = CATALOGO_ETAPAS_INFO.reduce((acc, etapa) => {
  (acc[etapa.fase] = acc[etapa.fase] || []).push(etapa);
  return acc;
}, {});

/** {codigo: entrada do catálogo} — lookup fase/label/ordem por código. */
export const ETAPA_INFO_POR_CODIGO = CATALOGO_ETAPAS_INFO.reduce(
  (acc, etapa) => {
    acc[etapa.codigo] = etapa;
    return acc;
  },
  {},
);

/**
 * Rótulos das 23 etapas do catálogo fixo (producao/models.py —
 * CATALOGO_ETAPAS). Usado onde o payload traz só o código (terminal,
 * eventos de bipagem); telas que têm `ambiente.etapas[]` preferem o
 * `label` do próprio payload.
 */
export const ETAPA_LABELS = {
  DEFINICAO_ACABAMENTO: "Definição de acabamento",
  MEDICAO_TECNICA: "Medição técnica",
  ELABORACAO: "Elaboração",
  REVISAO: "Revisão",
  APROVACAO: "Aprovação",
  AGRUPAR_LOTE: "Agrupar lote",
  PLANO_CORTE: "Plano de corte",
  GERAR_BOM: "Gerar BOM",
  REQUISICAO: "Requisição",
  RECEBIMENTO: "Recebimento",
  CORTE: "Corte",
  BORDO: "Bordo",
  FURACAO: "Furação",
  USINAGEM: "Usinagem",
  CAIXARIA: "Caixaria",
  DUPLAGEM: "Duplagem",
  PINTURA: "Pintura",
  ELETRICA: "Elétrica",
  PRE_MONTAGEM: "Pré-montagem",
  CONFERENCIA: "Conferência",
  LIMPEZA: "Limpeza",
  SEPARACAO: "Separação",
  CARREGAMENTO: "Carregamento",
};

/**
 * Permissão de OPERAR etapas por fase (Lote B — D1.1): 6 códigos
 * `producao_fase:operar_f1..f6`, um por fase operacional. Master tem
 * bypass no próprio `hasPermission` do AuthContext. O gate real é do
 * backend — aqui é UX (esconder botão/drag de quem não opera a fase).
 */
export const PERMISSAO_OPERAR_POR_FASE = {
  DEFINICOES_INICIAIS: "producao_fase:operar_f1",
  PROJETO_EXECUTIVO: "producao_fase:operar_f2",
  OTIMIZACAO: "producao_fase:operar_f3",
  COMPRAS: "producao_fase:operar_f4",
  INDUSTRIALIZACAO: "producao_fase:operar_f5",
  MARCENARIA: "producao_fase:operar_f6",
};

/** Código da permissão de operar a fase (null p/ marcos, que não operam). */
export function permissaoOperarFase(fase) {
  return PERMISSAO_OPERAR_POR_FASE[fase] || null;
}

/**
 * Permissão extra (além da fase) para CONCLUIR a etapa, espelhando
 * `concluir_etapa` em producao/views_v2.py: concluir o Carregamento encerra
 * a produção do ambiente e exige `ambiente_producao:carregar`. Sem ela o
 * botão Concluir some — antes aparecia e o backend respondia 403.
 */
const PERMISSAO_EXTRA_CONCLUIR_POR_ETAPA = {
  CARREGAMENTO: "ambiente_producao:carregar",
};

export function permissaoExtraConcluirEtapa(codigoEtapa) {
  return PERMISSAO_EXTRA_CONCLUIR_POR_ETAPA[codigoEtapa] || null;
}

/**
 * Checkpoints de bipagem (Lote B — §2.5): etapas que SÓ concluem pela
 * bipagem no Terminal (peça a peça). A lista efetiva vem da chave
 * `checkpoints_bipagem` da Config (`useCheckpointsBipagem`); este é o
 * default do catálogo — usado como fallback quando a Config não está
 * acessível (ex.: usuário sem ppcp:view).
 */
export const DEFAULT_CHECKPOINTS_BIPAGEM = ["USINAGEM", "SEPARACAO"];

/** Chave do parâmetro da Config que lista os checkpoints. */
export const CHAVE_CHECKPOINTS_BIPAGEM = "checkpoints_bipagem";

/** Papel de cada checkpoint default no fluxo (copy do Terminal/Detalhe). */
export const CHECKPOINT_DESCRICOES = {
  USINAGEM: "saída da Industrialização",
  SEPARACAO: "conferência de embarque",
};

/**
 * Rótulo amigável de um checkpoint: "Usinagem — saída da Industrialização".
 * Checkpoint fora do default (configurado na Config) cai no label genérico
 * do catálogo.
 */
export function rotuloCheckpoint(codigo) {
  if (!codigo) return "—";
  const base = ETAPA_LABELS[codigo] || codigo;
  const papel = CHECKPOINT_DESCRICOES[codigo];
  return papel ? `${base} — ${papel}` : base;
}

/**
 * O 400 do backend ao concluir manualmente etapa-checkpoint traz
 * `detalhe` mencionando o checkpoint de bipagem. Fallback do front
 * quando a lista local está desatualizada em relação à Config.
 */
export function erroEhCheckpointBipagem(err) {
  const detalhe = String(err?.payload?.detalhe || err?.message || "");
  return /checkpoint/i.test(detalhe)
    || /conclui\w* (por|pela|via) bipagem/i.test(detalhe);
}

export const ESTADO_ETAPA_LABELS = {
  PENDENTE: "Pendente",
  EM_EXECUCAO: "Em execução",
  CONCLUIDA: "Concluída",
  NAO_APLICAVEL: "n/a",
};

export const ESTADO_ETAPA_TONES = {
  PENDENTE: "neutral",
  EM_EXECUCAO: "info",
  CONCLUIDA: "success",
  NAO_APLICAVEL: "neutral",
};

/** Zonas do fever chart de buffer (D4) — cor só comunica estado. */
export const BUFFER_ZONA_TONES = {
  verde: "success",
  amarelo: "warning",
  vermelho: "danger",
};

// ============================================================
// Painel (board + KPIs)
// ============================================================

/**
 * GET /api/producao/board?unidade= — 23 colunas fixas (ordem 1..23), cada
 * ambiente ativo na coluna da sua etapa corrente.
 * Retorna { etapas: [{codigo, fase, label, ordem, bipavel, ambientes[]}],
 * total_ambientes }.
 */
export function boardPorEtapa(request, { unidade } = {}) {
  const usp = new URLSearchParams();
  if (unidade) usp.set("unidade", unidade);
  const qs = usp.toString();
  return request(`${PREFIXO_PAINEL}/board${qs ? `?${qs}` : ""}`, {
    silentError: true,
  });
}

/**
 * GET /api/producao/kpis?unidade= — { wip, carregam_hoje, em_atraso,
 * anomalias, buffer_critico }.
 */
export function kpisPainel(request, { unidade } = {}) {
  const usp = new URLSearchParams();
  if (unidade) usp.set("unidade", unidade);
  const qs = usp.toString();
  return request(`${PREFIXO_PAINEL}/kpis${qs ? `?${qs}` : ""}`, {
    silentError: true,
  });
}

/**
 * GET /api/pedidos/{id} — dados comerciais do pedido para o AGREGADOR
 * (/producao/pedidos/:id): cliente, situação, valor, datas, vendedor.
 * Espelho de services/pedidos.obterPedido, mas com silentError: aqui o
 * pedido é ENRIQUECIMENTO — sem ele a tela degrada para os dados dos
 * ambientes, sem toast (ex.: usuário com ambiente_producao:view mas sem
 * pedido:view).
 */
export function obterPedidoComercial(request, id) {
  return request(`/api/pedidos/${id}`, { silentError: true });
}

// ============================================================
// Ambientes de produção
// ============================================================

/**
 * GET /api/ambientes-producao?fase=&congelado=&pedido=&busca=&atrasados=
 * Lista (serializer compacto: campos do card + pedido + fase_display).
 */
export function listarAmbientes(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.fase) usp.set("fase", params.fase);
  if (params.congelado !== undefined && params.congelado !== null) {
    usp.set("congelado", String(params.congelado));
  }
  if (params.pedido) usp.set("pedido", params.pedido);
  if (params.busca) usp.set("busca", params.busca);
  if (params.atrasados) usp.set("atrasados", "true");
  const qs = usp.toString();
  return request(`${PREFIXO_AMBIENTES}${qs ? `?${qs}` : ""}`, {
    silentError: true,
  });
}

/**
 * GET /api/ambientes-producao/{id} — Detalhe completo (etapas[] + atrasos[]
 * + buffer{total_min, consumido_min, pct, zona} + congelamento + cancelamento).
 */
export function obterAmbiente(request, id) {
  return request(`${PREFIXO_AMBIENTES}/${id}`, { silentError: true });
}

/**
 * PATCH /api/ambientes-producao/{id}
 * body: { data_alvo_carregamento?, responsavel?, observacoes?,
 * buffer_total_min? }.
 */
export function atualizarAmbiente(request, id, dados) {
  return request(`${PREFIXO_AMBIENTES}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

/**
 * GET /api/ambientes-producao/{id}/timeline?limite=N — Linha do tempo
 * agregada do ambiente (Lote A — auditoria §2.3): junta auditoria por alvo
 * + bipagens (AGRUPADAS por etapa no tipo `bipagens_etapa`) + atrasos +
 * marcos do lote. Retorna { eventos: [{tipo, icone_hint, titulo, detalhe,
 * ator_nome, timestamp, dados}], omitidos } — `omitidos > 0` habilita o
 * "Carregar mais antigos" (o caller repete com limite maior).
 */
export function timelineAmbiente(request, id, { limite = 200 } = {}) {
  const usp = new URLSearchParams({ limite: String(limite) });
  return request(`${PREFIXO_AMBIENTES}/${id}/timeline?${usp.toString()}`, {
    silentError: true,
  });
}

/**
 * GET /api/ambientes-producao/{id}/bom — BOM estimado × executivo com delta
 * (Lote A — auditoria §2.2 / I-7). Estimado = baseline de venda (agregado
 * do metadata.composicao do orçamento); executivo = peças materializadas
 * (ativos ± reposições/canceladas) + lote {numero, origem, sobra_percentual};
 * delta calculado pelos 3 sinais reais (reposições, cancelamentos, sobra) —
 * só de chapas (D2.2). Campos de custo vêm AUSENTES quando o usuário não
 * tem `preco:view` (filtro no serializer — o gate do front é só UX).
 */
export function bomAmbiente(request, id) {
  return request(`${PREFIXO_AMBIENTES}/${id}/bom`, { silentError: true });
}

/**
 * Tenta baixar de novo o material das etapas consumidoras já concluídas.
 *
 * Caminho de volta da pendência "concluiu a etapa sem baixar estoque": o
 * hook só roda na conclusão, e ela já aconteceu — sem isto, corrigir o
 * cadastro do produto não teria efeito nenhum. Idempotente no backend.
 */
export function reprocessarConsumo(request, id) {
  return request(`${PREFIXO_AMBIENTES}/${id}/reprocessar-consumo`, {
    method: "POST",
  });
}

// ---------- actions de transição (retornam o Detalhe atualizado) ----------

export function congelarAmbiente(request, id, { motivo, observacao } = {}) {
  return request(`${PREFIXO_AMBIENTES}/${id}/congelar`, {
    method: "POST",
    body: JSON.stringify({ motivo, observacao: observacao || "" }),
  });
}

export function descongelarAmbiente(request, id) {
  return request(`${PREFIXO_AMBIENTES}/${id}/descongelar`, {
    method: "POST",
  });
}

/**
 * `fornecedorId` (opcional) vincula a ocorrência a um Parceiro
 * (/api/parceiros) quando a causa é de terceiro (meio externo) —
 * alimenta o ranking "Por fornecedor" da Análise de Atrasos (C7).
 */
export function registrarAtrasoAmbiente(
  request, id, { minutos, categoria, etapaId, descricao, fornecedorId } = {},
) {
  const body = { minutos, categoria, descricao: descricao || "" };
  if (etapaId) body.etapa_id = etapaId;
  if (fornecedorId) body.fornecedor_id = fornecedorId;
  return request(`${PREFIXO_AMBIENTES}/${id}/registrar-atraso`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Cancelamento é irreversível e exige MFA recente (step-up global). */
export function cancelarAmbiente(request, id, motivo, natureza) {
  return request(`${PREFIXO_AMBIENTES}/${id}/cancelar`, {
    method: "POST",
    body: JSON.stringify({ motivo, natureza }),
  });
}

/**
 * `silentError: true` suprime o auto-toast global — usado por telas que
 * tratam o erro INLINE (Detalhe/Minhas etapas), evitando mensagem dupla.
 * Default mantém o auto-toast (Painel/board).
 */
export function iniciarEtapa(request, id, etapaId, { silentError } = {}) {
  return request(`${PREFIXO_AMBIENTES}/${id}/iniciar-etapa`, {
    method: "POST",
    body: JSON.stringify({ etapa_id: etapaId }),
    silentError: Boolean(silentError),
  });
}

export function concluirEtapa(request, id, etapaId, { silentError } = {}) {
  return request(`${PREFIXO_AMBIENTES}/${id}/concluir-etapa`, {
    method: "POST",
    body: JSON.stringify({ etapa_id: etapaId }),
    silentError: Boolean(silentError),
  });
}

// ============================================================
// Otimização & Lotes (Sprint P2 — brief 0.11: dois caminhos,
// a produção NUNCA trava sem PharuCut)
// ============================================================

const PREFIXO_LOTES = "/api/lotes-producao";

export const SITUACAO_LOTE_LABELS = {
  ABERTO: "Aberto",
  OTIMIZADO: "Otimizado",
  LIBERADO: "Liberado",
};

export const SITUACAO_LOTE_TONES = {
  ABERTO: "neutral",
  OTIMIZADO: "info",
  LIBERADO: "success",
};

export const ORIGEM_LOTE_LABELS = {
  EXTERNO: "Externo (registro manual)",
  DASHCUT: "PharuCut",
};

/** Opções do select de origem no fluxo "Criar lote" (default EXTERNO). */
export const ORIGENS_LOTE = [
  {
    codigo: "EXTERNO",
    label: "Otimização externa (3D / otimizador próprio)",
  },
  {
    codigo: "DASHCUT",
    label: "PharuCut (mock — integração real em desenvolvimento)",
  },
];

/**
 * GET /api/producao/ambientes-elegiveis?unidade= — pool da Otimização:
 * ambientes ativos com Aprovação concluída e sem lote ativo.
 * Retorna { ambientes: [card do board], total }.
 */
export function ambientesElegiveis(request, { unidade } = {}) {
  const usp = new URLSearchParams();
  if (unidade) usp.set("unidade", unidade);
  const qs = usp.toString();
  return request(`${PREFIXO_PAINEL}/ambientes-elegiveis${qs ? `?${qs}` : ""}`, {
    silentError: true,
  });
}

/** GET /api/lotes-producao?situacao= — listagem compacta. */
export function listarLotes(request, { situacao } = {}) {
  const usp = new URLSearchParams();
  if (situacao) usp.set("situacao", situacao);
  const qs = usp.toString();
  return request(`${PREFIXO_LOTES}${qs ? `?${qs}` : ""}`, {
    silentError: true,
  });
}

/**
 * GET /api/lotes-producao/{id} — Detalhe (ambientes nested + requisicao +
 * plano_corte + pecas_count).
 */
export function obterLote(request, id) {
  return request(`${PREFIXO_LOTES}/${id}`, { silentError: true });
}

// ---------- actions do fluxo do lote (retornam o Detalhe; 400 {detalhe}) ----

/**
 * POST /api/lotes-producao/create-com-ambientes
 * body: { ambiente_ids: [uuid], origem?: EXTERNO|DASHCUT } — cria ABERTO
 * (201) e conclui a etapa Agrupar lote dos ambientes.
 */
export function criarLote(request, { ambienteIds, origem = "EXTERNO" } = {}) {
  return request(`${PREFIXO_LOTES}/create-com-ambientes`, {
    method: "POST",
    body: JSON.stringify({ ambiente_ids: ambienteIds || [], origem }),
  });
}

/**
 * POST /api/lotes-producao/{id}/registrar-plano — caminho manual/externo:
 * body { plano?: dict, sobra_percentual?: decimal }. Lote vira OTIMIZADO.
 */
export function registrarPlano(request, id, { plano, sobraPercentual } = {}) {
  const body = {};
  if (plano !== undefined && plano !== null) body.plano = plano;
  if (sobraPercentual !== undefined && sobraPercentual !== null
      && sobraPercentual !== "") {
    body.sobra_percentual = sobraPercentual;
  }
  return request(`${PREFIXO_LOTES}/${id}/registrar-plano`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * POST /api/lotes-producao/{id}/otimizar-mock — caminho integrado via
 * CutPlanProvider (body { provider?: str } — default "mock"; PharuCut real
 * é roadmap). Marca origem=DASHCUT e persiste o plano.
 */
export function otimizarMock(request, id, { provider } = {}) {
  const body = {};
  if (provider) body.provider = provider;
  return request(`${PREFIXO_LOTES}/${id}/otimizar-mock`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * POST /api/lotes-producao/{id}/gerar-requisicao — RC do BOM agregado do
 * lote (idempotente; exige OTIMIZADO).
 */
export function gerarRequisicao(request, id) {
  return request(`${PREFIXO_LOTES}/${id}/gerar-requisicao`, {
    method: "POST",
  });
}

/**
 * POST /api/lotes-producao/{id}/liberar — OTIMIZADO -> LIBERADO:
 * materializa as peças de produção dos ambientes do lote.
 */
export function liberarLote(request, id) {
  return request(`${PREFIXO_LOTES}/${id}/liberar`, {
    method: "POST",
  });
}

// ---------- etapa do LOTE em bloco (Lote C — card-lote atômico §2.4) ----

/**
 * POST /api/lotes-producao/{id}/iniciar-etapa — inicia a etapa corrente do
 * LOTE, cascateando em bloco para os ambientes membros aplicáveis.
 * body {etapa_codigo}; resposta {concluidos, pulados: [{ambiente, motivo}]}
 * (membro congelado/fora da etapa é pulado com motivo — parcial é fluxo
 * esperado, não erro). 403 sem producao_fase:operar_f{n} da fase da etapa;
 * 400 quando a etapa é checkpoint de bipagem (conclui no Terminal).
 * `silentError` p/ telas que tratam o erro inline (Detalhe do Lote);
 * default mantém o auto-toast global (board do Painel).
 */
export function iniciarEtapaLote(
  request, id, etapaCodigo, { silentError } = {},
) {
  return request(`${PREFIXO_LOTES}/${id}/iniciar-etapa`, {
    method: "POST",
    body: JSON.stringify({ etapa_codigo: etapaCodigo }),
    silentError: Boolean(silentError),
  });
}

/**
 * POST /api/lotes-producao/{id}/concluir-etapa — conclui a etapa corrente
 * do LOTE em bloco (mesmo contrato/erros de iniciarEtapaLote).
 */
export function concluirEtapaLote(
  request, id, etapaCodigo, { silentError } = {},
) {
  return request(`${PREFIXO_LOTES}/${id}/concluir-etapa`, {
    method: "POST",
    body: JSON.stringify({ etapa_codigo: etapaCodigo }),
    silentError: Boolean(silentError),
  });
}

// ============================================================
// Bipagem v2 / peças (Sprint P3)
// ============================================================

/**
 * GET /api/postos-bipagem?ativo=&etapa_codigo=&unidade= — CRUD gerencial
 * (auth JWT, gate posto_bipagem:view). O terminal kiosk usa o endpoint
 * público próprio (pages/Terminal/client.js).
 */
export function listarPostosBipagem(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.ativo !== undefined && params.ativo !== null) {
    usp.set("ativo", String(params.ativo));
  }
  if (params.etapaCodigo) usp.set("etapa_codigo", params.etapaCodigo);
  if (params.unidade) usp.set("unidade", params.unidade);
  const qs = usp.toString();
  return request(`/api/postos-bipagem${qs ? `?${qs}` : ""}`, {
    silentError: true,
  });
}

/**
 * POST /api/ordens-producao/{id}/registrar-reposicao — reposição/refazer
 * com aresta pai->filha (D3): body { motivo_categoria: 6M, descricao }.
 * Cancela a peça-pai e cria a filha (eh_reposicao, fora do throughput).
 * Gate ordem_producao:reposicao + MFA recente (step-up global). 201 com o
 * Detalhe da peça-filha.
 */
export function registrarReposicaoPeca(
  request, pecaId, { motivoCategoria, descricao } = {},
) {
  return request(`/api/ordens-producao/${pecaId}/registrar-reposicao`, {
    method: "POST",
    body: JSON.stringify({
      motivo_categoria: motivoCategoria,
      descricao,
    }),
  });
}

// ============================================================
// Disposição de peças (Lote D — auditoria §2.7: peça reprovada na
// Conferência fica RETIDA até o PCP decidir repor/retrabalhar/aceitar)
// ============================================================

/** Rótulos das 3 decisões de disposição (fila + drawer da peça). */
export const DECISAO_DISPOSICAO_LABELS = {
  repor: "Repor (nova peça)",
  retrabalhar: "Retrabalhar",
  aceitar: "Aceitar (com concessão)",
};

/** Tone da Pill por decisão — aceitar é concessão (warning consciente). */
export const DECISAO_DISPOSICAO_TONES = {
  repor: "info",
  retrabalhar: "info",
  aceitar: "warning",
};

/**
 * GET /api/producao/disposicao (gate ppcp:view) — fila de peças retidas:
 * { pecas: [{id, numero, descricao, ambiente: {id, numero, nome},
 * pedido_numero, cliente_nome, qtd_refugo, ncs: [{categoria, tipo,
 * descricao}], retida_desde}] }.
 */
export function filaDisposicao(request) {
  return request(`${PREFIXO_PAINEL}/disposicao`, { silentError: true });
}

/**
 * POST /api/ordens-producao/{id}/disposicao (gate ordem_producao:reposicao)
 * body: { decisao: "repor"|"retrabalhar"|"aceitar", observacao }.
 * `repor` reusa registrar_reposicao no backend e exige a categoria 6M —
 * enviamos `categoria` E `motivo_categoria` (mesmo valor) para casar com
 * qualquer um dos dois nomes do contrato (DRF ignora chave extra).
 */
export function decidirDisposicaoPeca(
  request, pecaId, { decisao, observacao, categoria } = {},
) {
  const body = { decisao, observacao: observacao || "" };
  if (categoria) {
    body.categoria = categoria;
    body.motivo_categoria = categoria;
  }
  return request(`/api/ordens-producao/${pecaId}/disposicao`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ============================================================
// KPIs PPCP (DBR) + Análise de Atrasos (Sprint P4)
// ============================================================

/** Janelas aceitas pelo endpoint DBR (e default persistido da tela). */
export const JANELAS_DBR = ["7d", "30d", "90d"];

/**
 * GET /api/producao/dbr?janela=7d|30d|90d&unidade= — payload único da tela
 * KPIs PPCP (tambor, wip, throughput, corda, pulmao, despacho, criticos).
 * Métrica sem dado suficiente vem `null` (o front mostra "—", nunca
 * inventa número — SEV-1).
 */
export function indicadoresDbr(request, { janela = "30d", unidade } = {}) {
  const usp = new URLSearchParams({ janela });
  if (unidade) usp.set("unidade", unidade);
  return request(`${PREFIXO_PAINEL}/dbr?${usp.toString()}`, {
    silentError: true,
  });
}

/**
 * GET /api/producao/atrasos/analise — Análise de Atrasos (Ishikawa).
 * `lente=tempo|qualidade`; filtros fase/etapa/categoria/responsavel/pedido
 * + `fornecedor` (id de Parceiro — só na lente tempo, C7);
 * `periodo=7d|30d|90d|custom` (custom exige inicio+fim YYYY-MM-DD);
 * paginação da tabela-fonte via pagina/por_pagina.
 */
export function analiseAtrasos(request, params = {}) {
  const usp = new URLSearchParams();
  const chaves = [
    "lente", "fase", "etapa", "categoria", "responsavel", "pedido",
    "fornecedor", "periodo", "inicio", "fim", "pagina", "por_pagina",
    "unidade",
  ];
  for (const chave of chaves) {
    const valor = params[chave];
    if (valor !== undefined && valor !== null && valor !== "") {
      usp.set(chave, String(valor));
    }
  }
  const qs = usp.toString();
  return request(`${PREFIXO_PAINEL}/atrasos/analise${qs ? `?${qs}` : ""}`, {
    silentError: true,
  });
}

// ============================================================
// Planejamento Marcenaria (Sprint P5 — board etapa x executor)
// ============================================================

/** Etapas de F6 Marcenaria, na ordem do roteiro (filtro da tela). */
export const ETAPAS_F6 = CATALOGO_ETAPAS_INFO.filter(
  (etapa) => etapa.fase === "MARCENARIA",
);

/** Tone do badge de nível do executor (brief: SR info · PL neutral · JR warning). */
export const NIVEL_OPERADOR_TONES = {
  SR: "info",
  PL: "neutral",
  JR: "warning",
};

export const NIVEIS_OPERADOR = [
  { codigo: "JR", label: "Júnior" },
  { codigo: "PL", label: "Pleno" },
  { codigo: "SR", label: "Sênior" },
];

export const FUNCOES_OPERADOR = [
  { codigo: "MARCENEIRO", label: "Marceneiro" },
  { codigo: "MEIO_OFICIAL", label: "Meio-oficial" },
  { codigo: "AUXILIAR", label: "Auxiliar" },
  { codigo: "OUTRO", label: "Outro" },
];

/** Motivos tipados de indisponibilidade do executor (contrato RH D8). */
export const MOTIVOS_INDISPONIBILIDADE = [
  { codigo: "FERIAS", label: "Férias" },
  { codigo: "FALTA", label: "Falta" },
  { codigo: "AFASTAMENTO", label: "Afastamento" },
  { codigo: "OUTRA_FABRICA", label: "Outra fábrica" },
  { codigo: "OUTRO", label: "Outro" },
];

/**
 * GET /api/producao/marcenaria/board?inicio=&fim=&unidade= (gate ppcp:view)
 * Payload único: periodo + colaboradores[{capacidade, indisponivel, cards}]
 * + sem_responsavel[] + aguardando_ose[] + kpis.
 */
export function boardMarcenaria(request, { inicio, fim, unidade } = {}) {
  const usp = new URLSearchParams();
  if (inicio) usp.set("inicio", inicio);
  if (fim) usp.set("fim", fim);
  if (unidade) usp.set("unidade", unidade);
  const qs = usp.toString();
  return request(`${PREFIXO_PAINEL}/marcenaria/board${qs ? `?${qs}` : ""}`, {
    silentError: true,
  });
}

/**
 * POST /api/producao/etapas/{id}/alocar (gate marcenaria:alocar)
 * body: {operador_id, janela_inicio, janela_fim (ISO), observacao?, forcar?}
 *
 * silentError proposital: o 422 de interferência é fluxo ESPERADO do form
 * ("Conflita com Duplagem 09:30–11:00" inline + botão "Alocar mesmo assim"
 * que reenvia com forcar:true). O caller lê err.status/err.payload.
 */
export function alocarEtapa(
  request,
  etapaId,
  { operadorId, janelaInicio, janelaFim, observacao, forcar } = {},
) {
  const body = {
    operador_id: operadorId,
    janela_inicio: janelaInicio,
    janela_fim: janelaFim,
  };
  if (observacao) body.observacao = observacao;
  if (forcar) body.forcar = true;
  return request(`${PREFIXO_PAINEL}/etapas/${etapaId}/alocar`, {
    method: "POST",
    body: JSON.stringify(body),
    silentError: true,
  });
}

/** POST /api/producao/etapas/{id}/desalocar — volta a "sem responsável". */
export function desalocarEtapa(request, etapaId) {
  return request(`${PREFIXO_PAINEL}/etapas/${etapaId}/desalocar`, {
    method: "POST",
  });
}

/** GET /api/operadores/{id}/indisponibilidades — {indisponibilidades: []}. */
export function listarIndisponibilidades(request, operadorId) {
  return request(`/api/operadores/${operadorId}/indisponibilidades`, {
    silentError: true,
  });
}

/**
 * POST /api/operadores/{id}/indisponibilidades (gate operador:manage)
 * body: {inicio: YYYY-MM-DD, fim: YYYY-MM-DD, motivo, observacao?}.
 */
export function criarIndisponibilidadeOperador(
  request, operadorId, { inicio, fim, motivo, observacao } = {},
) {
  return request(`/api/operadores/${operadorId}/indisponibilidades`, {
    method: "POST",
    body: JSON.stringify({
      inicio,
      fim,
      motivo,
      observacao: observacao || "",
    }),
  });
}

/** DELETE /api/operadores/{id}/indisponibilidades/{iid} (operador:manage). */
export function removerIndisponibilidadeOperador(
  request, operadorId, indisponibilidadeId,
) {
  return request(
    `/api/operadores/${operadorId}/indisponibilidades/${indisponibilidadeId}`,
    { method: "DELETE" },
  );
}

// ============================================================
// Configurações da Produção (Sprint P5 — catálogo essencial)
// ============================================================

/** Drivers válidos do coeficiente prior (brief 3.3). */
export const DRIVERS_COEFICIENTE = [
  { codigo: "min_m2", label: "min/m²" },
  { codigo: "min_furo", label: "min/furo" },
  { codigo: "min_m_fita", label: "min/m fita" },
  { codigo: "min_modulo", label: "min/módulo" },
  { codigo: "min_peca", label: "min/peça" },
  { codigo: "min_fixo", label: "min fixo" },
];

/**
 * GET /api/producao/config/catalogo (gate ppcp:view) — catálogo declarativo
 * + valor efetivo + origem ("default" | "personalizado") por chave.
 */
export function configCatalogo(request, { unidade } = {}) {
  const usp = new URLSearchParams();
  if (unidade) usp.set("unidade", unidade);
  const qs = usp.toString();
  return request(`${PREFIXO_PAINEL}/config/catalogo${qs ? `?${qs}` : ""}`, {
    silentError: true,
  });
}

/**
 * PUT /api/producao/config/parametros (gate ppcp:configurar)
 * body: {parametros: {chave: valor}}. 400 com {detalhe, erros: {chave: msg}}
 * — silentError: a savebar da seção mostra o erro por chave inline.
 */
export function salvarParametrosConfig(request, parametros, { unidade } = {}) {
  const body = { parametros };
  if (unidade) body.unidade = unidade;
  return request(`${PREFIXO_PAINEL}/config/parametros`, {
    method: "PUT",
    body: JSON.stringify(body),
    silentError: true,
  });
}

/**
 * GET /api/producao/config/coeficientes-medidos — motor empírico (medido):
 * {n_minimo_amostras, etapas: [{codigo, label, fase, mediana_touch_min,
 * n_amostras, confiavel, prior}]}. Sem amostra -> mediana null (nunca
 * número inventado).
 */
export function coeficientesMedidos(request, { unidade } = {}) {
  const usp = new URLSearchParams();
  if (unidade) usp.set("unidade", unidade);
  const qs = usp.toString();
  return request(
    `${PREFIXO_PAINEL}/config/coeficientes-medidos${qs ? `?${qs}` : ""}`,
    { silentError: true },
  );
}

/**
 * GET /api/producao/config/cobertura-postos — etapas bipáveis sem posto
 * ativo + tambor descoberto (banner de cobertura da seção Postos).
 */
export function coberturaPostos(request, { unidade } = {}) {
  const usp = new URLSearchParams();
  if (unidade) usp.set("unidade", unidade);
  const qs = usp.toString();
  return request(
    `${PREFIXO_PAINEL}/config/cobertura-postos${qs ? `?${qs}` : ""}`,
    { silentError: true },
  );
}

// ---------- CRUD de postos (PostoBipagemViewSet — posto_bipagem:manage) ----

/** POST /api/postos-bipagem — body {nome, etapa_codigo, unidade, ativo?}. */
export function criarPostoBipagem(request, dados) {
  return request("/api/postos-bipagem", {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

/** PATCH /api/postos-bipagem/{id} — inativar é soft (ativo: false). */
export function atualizarPostoBipagem(request, id, dados) {
  return request(`/api/postos-bipagem/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}
